import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { MessageSquare, Send, Loader2, Lock } from 'lucide-react';

export const SimulationChat = ({ simulationId }) => {
  const { api, user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef(null);

  const fetchMessages = useCallback(async () => {
    try {
      const res = await api().get(`/simulations/${simulationId}/chat`);
      setMessages(res.data?.messages || []);
    } catch (e) { /* accès refusé / réseau : silencieux */ }
    finally { setLoading(false); }
  }, [api, simulationId]);

  useEffect(() => {
    fetchMessages();
    const t = setInterval(fetchMessages, 10000);
    return () => clearInterval(t);
  }, [fetchMessages]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async () => {
    const val = text.trim();
    if (!val || sending) return;
    setSending(true);
    try {
      const res = await api().post(`/simulations/${simulationId}/chat`, { text: val });
      setMessages((prev) => [...prev, res.data]);
      setText('');
    } catch (e) { /* silencieux */ }
    finally { setSending(false); }
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString('fr-FR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); }
    catch { return ''; }
  };

  return (
    <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden" data-testid="simulation-chat">
      <div className="flex items-center gap-2 px-4 py-3 border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
        <MessageSquare className="w-4 h-4 text-orange-500" />
        <h3 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Échanges sur le dossier</h3>
        <span className="ml-auto flex items-center gap-1 text-[11px] text-slate-400"><Lock className="w-3 h-3" /> messages conservés</span>
      </div>

      <div ref={scrollRef} className="max-h-80 overflow-y-auto px-4 py-3 space-y-3" data-testid="chat-messages">
        {loading ? (
          <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-300" /></div>
        ) : messages.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-6">Aucun message. Démarrez la conversation avec le commercial.</p>
        ) : (
          messages.map((m) => {
            const mine = m.author_id === user?.id;
            return (
              <div key={m.id} className={`flex ${mine ? 'justify-end' : 'justify-start'}`} data-testid={`chat-msg-${m.id}`}>
                <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${mine ? 'bg-orange-500 text-white border border-orange-300/70 rounded-br-sm shadow-[0_0_10px_rgba(249,115,22,0.7),0_0_22px_rgba(249,115,22,0.45)]' : 'bg-slate-100 dark:bg-slate-800 text-slate-800 dark:text-slate-100 rounded-bl-sm'}`}>
                  <div className={`text-[11px] mb-0.5 font-medium ${mine ? 'text-orange-50' : 'text-slate-500'}`}>{m.author_name}</div>
                  <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>
                  <div className={`text-[10px] mt-1 text-right ${mine ? 'text-orange-100' : 'text-slate-400'}`}>{fmt(m.created_at)}</div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="flex items-end gap-2 border-t border-slate-100 dark:border-slate-800 p-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Écrire un message…  (Entrée pour envoyer)"
          rows={1}
          className="flex-1 resize-none rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400 max-h-28"
          data-testid="chat-input"
        />
        <button
          onClick={send}
          disabled={sending || !text.trim()}
          className="shrink-0 h-9 w-9 flex items-center justify-center rounded-xl bg-orange-500 text-white disabled:opacity-40 hover:bg-orange-600 transition-colors"
          data-testid="chat-send-btn"
        >
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </button>
      </div>
    </div>
  );
};

export default SimulationChat;
